import React, {useContext, useEffect, useState} from 'react';
import styles from '../../styles/dashboard.module.css';
import Header from "../../components/header/header";
import PageTitle from "../../components/page_title/page_title";
import GPSTabs from "./components/gps_tabs";
import axiosInstance from "../../AxiosInstance";
import Modal from "../../components/modal/modal";
import {TextInput} from "../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash, faEdit, faPlus, faToggleOn, faToggleOff, faTriangleExclamation, faPlay, faSpinner} from "@fortawesome/free-solid-svg-icons";
import MyContext from "../../Context";
import PhoneInput from "react-phone-input-2";
import {PlaceholderTextarea, PlaceholderInput} from "../../components/input/PlaceholderTextarea";
import {useTranslation} from 'react-i18next';

const RULE_PLACEHOLDERS = ['{asset_name}', '{identification_number}', '{device_sn}', '{zone_name}', '{trigger}', '{rule_name}'];

function normalizePlaceholders(text) {
    let result = text;
    let prev;
    do {
        prev = result;
        result = result
            .replace(/"(\{[^{}]+\})"/g, '"__x__"')
            .replace(/\{[^{}]+\}/g, '"__x__"');
    } while (result !== prev);
    return result;
}

function tryParseJson(text) {
    if (!text || !text.trim()) return null;
    try {
        JSON.parse(normalizePlaceholders(text));
        return null;
    } catch (e) {
        return e.message;
    }
}

const jsonLabelStyle = {fontSize: '12px', fontWeight: '600', color: '#64748B', marginBottom: '4px', display: 'block'};
const jsonInputStyle = {width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #E2E8F0', fontSize: '13px', outline: 'none', color: '#334155', boxSizing: 'border-box'};

function JsonTextarea({label, value, onChange, placeholder, placeholders}) {
    const {t} = useTranslation();
    const error = tryParseJson(value);

    const formatJson = () => {
        if (!value || !value.trim()) return;
        const tokens = {};
        let i = 0;
        let tokenized = value;
        let prev;
        do {
            prev = tokenized;
            tokenized = tokenized
                .replace(/"(\{[^{}]+\})"/g, (match) => {
                    const k = `__PH${i++}__`;
                    tokens[`"${k}"`] = match;
                    return `"${k}"`;
                })
                .replace(/\{[^{}]+\}/g, m => {
                    const k = `"__PH${i++}__"`;
                    tokens[k] = m;
                    return k;
                });
        } while (tokenized !== prev);
        try {
            const pretty = JSON.stringify(JSON.parse(tokenized), null, 2);
            const restored = pretty.replace(/"__PH\d+__"/g, m => tokens[m] || m);
            onChange(restored);
        } catch (_) {}
    };

    return (
        <div style={{marginBottom: '10px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px'}}>
                <span style={jsonLabelStyle}>{label}</span>
                <button
                    type="button" onClick={formatJson}
                    disabled={!value || !!error}
                    style={{
                        fontSize: '11px', padding: '2px 8px', borderRadius: '4px', cursor: 'pointer',
                        border: '1px solid #C7D2FE', background: '#EEF2FF', color: '#4F46E5',
                        fontWeight: 600, opacity: (!value || !!error) ? 0.4 : 1,
                    }}
                >
                    {t('gps.rules.formatJson')}
                </button>
            </div>
            <PlaceholderTextarea
                value={value || ''}
                onChange={onChange}
                placeholder={placeholder}
                placeholders={placeholders}
                style={{
                    ...jsonInputStyle,
                    fontFamily: 'monospace', fontSize: '12px',
                    minHeight: '60px', resize: 'vertical', marginBottom: error ? '2px' : '0',
                    border: `1px solid ${error ? '#EF4444' : '#E2E8F0'}`,
                    backgroundColor: error ? '#FFF8F8' : '#fff',
                }}
            />
            {error && (
                <div style={{fontSize: '11px', color: '#EF4444', marginBottom: '6px'}}>
                    {t('gps.rules.invalidJson', {error})}
                </div>
            )}
        </div>
    );
}

const TRIGGER_OPTIONS = [
    {id: 'enter', labelKey: 'gps.rules.entersZone'},
    {id: 'leave', labelKey: 'gps.rules.leavesZone'},
    {id: 'overtime', labelKey: 'gps.rules.overtimeInZone'},
];

const ACTION_TYPE_OPTIONS = [
    {id: 1, labelKey: 'gps.rules.actionTypes.email'},
    {id: 3, labelKey: 'gps.rules.actionTypes.sms'},
    {id: 4, labelKey: 'gps.rules.actionTypes.whatsapp'},
    {id: 5, labelKey: 'gps.rules.actionTypes.mqtt'},
    {id: 6, labelKey: 'gps.rules.actionTypes.api'},
];

export default function GPSRules() {
    const {t} = useTranslation();
    const project_id = parseInt(localStorage.getItem('project_id'));
    const [rules, setRules] = useState([]);
    const [zones, setZones] = useState([]);
    const [assets, setAssets] = useState([]);
    const [groups, setGroups] = useState([]);
    const [mqttConnections, setMqttConnections] = useState([]);
    const [apiConnections, setApiConnections] = useState([]);
    const [gpsActions, setGpsActions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [form, setForm] = useState({
        name: '',
        zone: '',
        trigger: 'enter',
        overtime_minutes: 0,
        show_warning: false,
        send_notification: false,
        action_type: 1,
        action_data_1: '',
        action_data_2: '',
        action_data_3: '',
        action_data_4: '',
        mqtt_connection: '',
        api_connection: '',
        is_active: true,
        boolean_format: 'raw',
        send_sos: false,
    });
    const [targets, setTargets] = useState({type: 'individuals', individual_ids: [], group_ids: []});
    const [overtimeWarning, setOvertimeWarning] = useState('');
    const {corporateGroup, systemSettings} = useContext(MyContext);
    const visibleActionTypes = ACTION_TYPE_OPTIONS.filter(a =>
        (a.id !== 3 || systemSettings?.sms_enabled) &&
        (a.id !== 4 || systemSettings?.whatsapp_enabled)
    );
    const [testingRuleId, setTestingRuleId] = useState(null);
    const [sosConfirmPending, setSosConfirmPending] = useState(false);
    const [deletingRuleId, setDeletingRuleId] = useState(null);
    const [testResultMsg, setTestResultMsg] = useState(null);
    const [ruleError, setRuleError] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [assetSearch, setAssetSearch] = useState('');

    const fetchData = (mounted = {current: true}) => {
        if (!project_id) return;
        axiosInstance.get(`zone_rule/?project_id=${project_id}`).then(res => { if (mounted.current) setRules(res.data); });
        axiosInstance.get(`map_zone/?project_id=${project_id}`).then(res => { if (mounted.current) setZones(res.data); });
        axiosInstance.get(`gps_asset/?project_id=${project_id}`).then(res => { if (mounted.current) setAssets(res.data); });
        axiosInstance.get(`gps_asset_group/?project_id=${project_id}`).then(res => { if (mounted.current) setGroups(res.data); });
        axiosInstance.get(`mqtt_connection/?project_id=${project_id}`).then(res => { if (mounted.current) setMqttConnections(Array.isArray(res.data) ? res.data : (res.data.results || [])); }).catch(() => {});
        axiosInstance.get(`api_connection/?project_id=${project_id}`).then(res => { if (mounted.current) setApiConnections(Array.isArray(res.data) ? res.data : (res.data.results || [])); }).catch(() => {});
        axiosInstance.get(`gps_action/?project_id=${project_id}`).then(res => { if (mounted.current) setGpsActions(Array.isArray(res.data) ? res.data : (res.data.results || [])); }).catch(() => {});
    };

    useEffect(() => {
        const mounted = {current: true};
        fetchData(mounted);
        return () => { mounted.current = false; };
    }, [project_id]);

    // Check overtime vs zone max
    useEffect(() => {
        if (form.trigger !== 'overtime' || !form.zone || !form.overtime_minutes) {
            setOvertimeWarning('');
            return;
        }
        const zone = zones.find(z => z.id === parseInt(form.zone));
        if (zone && zone.max_overtime_minutes > 0 && parseInt(form.overtime_minutes) > zone.max_overtime_minutes) {
            setOvertimeWarning(`This zone has a max overtime of ${zone.max_overtime_minutes} min. The rule will use the zone's limit.`);
        } else {
            setOvertimeWarning('');
        }
    }, [form.trigger, form.zone, form.overtime_minutes, zones]);

    const openCreateModal = () => {
        setEditingRule(null);
        setForm({
            name: '', zone: '', trigger: 'enter', overtime_minutes: 0,
            show_warning: false, send_notification: false,
            action_type: 1, action_data_1: '', action_data_2: '', action_data_3: '', action_data_4: '',
            mqtt_connection: '', api_connection: '', is_active: true, boolean_format: 'raw', send_sos: false,
        });
        setTargets({type: 'individuals', individual_ids: [], group_ids: []});
        setOvertimeWarning('');
        setFormErrors({});
        setAssetSearch('');
        setShowModal(true);
    };

    const openEditModal = (rule) => {
        setEditingRule(rule);
        const hasNotification = !!rule.action_type;
        setForm({
            name: rule.name,
            zone: rule.zone,
            trigger: rule.trigger,
            overtime_minutes: rule.overtime_minutes || 0,
            show_warning: rule.show_warning || false,
            send_notification: hasNotification,
            action_type: rule.action_type || 1,
            action_data_1: rule.action_data_1 || '',
            action_data_2: rule.action_data_2 || '',
            action_data_3: rule.action_data_3 || '',
            action_data_4: rule.action_data_4 || '',
            mqtt_connection: rule.mqtt_connection || '',
            api_connection: rule.api_connection || '',
            is_active: rule.is_active,
            boolean_format: rule.boolean_format || 'raw',
            send_sos: rule.send_sos || false,
        });
        const ruleTargets = rule.targets || [];
        const individualIds = ruleTargets.filter(t => t.device_user).map(t => t.device_user);
        const groupIds = ruleTargets.filter(t => t.asset_group).map(t => t.asset_group);
        setTargets({
            type: groupIds.length > 0 ? 'groups' : 'individuals',
            individual_ids: individualIds,
            group_ids: groupIds,
        });
        setFormErrors({});
        setAssetSearch('');
        setShowModal(true);
    };

    const validateForm = () => {
        const errors = {};
        if (!form.name.trim()) errors.name = t('gps.rules.fieldRequired');
        if (!form.zone) errors.zone = t('gps.rules.fieldRequired');
        const hasTargets = targets.type === 'individuals' ? targets.individual_ids.length > 0 : targets.group_ids.length > 0;
        if (!hasTargets) errors.targets = t('gps.rules.targetRequired');
        if (!form.show_warning && !form.send_notification && !form.send_sos) errors.actions = t('gps.rules.actionRequired');
        if (form.trigger === 'overtime' && (!form.overtime_minutes || parseInt(form.overtime_minutes) < 1))
            errors.overtime_minutes = t('gps.rules.fieldRequired');
        if (form.send_notification) {
            const at = parseInt(form.action_type);
            if (at === 1) {
                if (!form.action_data_1.trim()) errors.action_data_1 = t('gps.rules.fieldRequired');
                if (!form.action_data_2.trim()) errors.action_data_2 = t('gps.rules.fieldRequired');
                if (!form.action_data_3.trim()) errors.action_data_3 = t('gps.rules.fieldRequired');
            } else if (at === 3 || at === 4) {
                if (!form.action_data_1 || form.action_data_1.replace(/\D/g, '').length < 7) errors.action_data_1 = t('gps.rules.fieldRequired');
                if (!form.action_data_2.trim()) errors.action_data_2 = t('gps.rules.fieldRequired');
            } else if (at === 5) {
                if (!form.mqtt_connection) errors.mqtt_connection = t('gps.rules.fieldRequired');
                if (!form.action_data_1.trim()) errors.action_data_1 = t('gps.rules.fieldRequired');
            } else if (at === 6) {
                if (!form.api_connection) errors.api_connection = t('gps.rules.fieldRequired');
                if (!form.action_data_1.trim()) errors.action_data_1 = t('gps.rules.fieldRequired');
            }
        }
        return errors;
    };

    const saveRule = (skipSosCheck = false) => {
        if (!skipSosCheck) {
            const errors = validateForm();
            if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
            setFormErrors({});
        }
        if (form.send_sos && selectedMissingSos.length > 0 && !skipSosCheck) {
            if (selectedCoveredSos.length === 0) {
                // Ninguno tiene SOS → bloquear completamente
                setRuleError(t('gps.zones.schedule.sosAllMissingError'));
                return;
            }
            // Algunos tienen SOS, otros no → pedir confirmación
            setSosConfirmPending(true);
            return;
        }
        setSosConfirmPending(false);
        const payload = {
            name: form.name,
            project: project_id,
            zone: parseInt(form.zone),
            trigger: form.trigger,
            overtime_minutes: form.trigger === 'overtime' ? parseInt(form.overtime_minutes) || 0 : 0,
            show_warning: form.show_warning,
            action_type: form.send_notification ? parseInt(form.action_type) : null,
            action_data_1: form.send_notification ? form.action_data_1 : '',
            action_data_2: form.send_notification ? form.action_data_2 : '',
            action_data_3: form.send_notification ? form.action_data_3 : '',
            action_data_4: form.send_notification ? form.action_data_4 : '',
            mqtt_connection: form.send_notification && parseInt(form.action_type) === 5 && form.mqtt_connection ? parseInt(form.mqtt_connection) : null,
            api_connection: form.send_notification && parseInt(form.action_type) === 6 && form.api_connection ? parseInt(form.api_connection) : null,
            is_active: form.is_active,
            boolean_format: form.boolean_format || 'raw',
            send_sos: form.send_sos,
            targets: targets.type === 'individuals'
                ? targets.individual_ids.map(id => ({device_user: id, asset_group: null}))
                : targets.group_ids.map(id => ({device_user: null, asset_group: id})),
        };

        const request = editingRule
            ? axiosInstance.put(`zone_rule/${editingRule.id}/`, payload)
            : axiosInstance.post('zone_rule/', payload);

        request.then(() => {
            setShowModal(false);
            fetchData();
        });
    };

    const deleteRule = (ruleId) => {
        setDeletingRuleId(ruleId);
    };

    const confirmDeleteRule = () => {
        axiosInstance.delete(`zone_rule/${deletingRuleId}/`).then(() => { setDeletingRuleId(null); fetchData({current: true}); });
    };

    const toggleActive = (rule) => {
        axiosInstance.patch(`zone_rule/${rule.id}/`, {is_active: !rule.is_active}).then(fetchData);
    };

    const testRule = (id) => {
        setTestingRuleId(id);
        axiosInstance.post(`zone_rule/${id}/test/`).then(res => {
            setTestingRuleId(null);
            setTestResultMsg(res.data.message || t('gps.rules.testSent'));
        }).catch(err => {
            setTestingRuleId(null);
            setTestResultMsg(err.response?.data?.error || t('gps.rules.testFailed'));
        });
    };

    const toggleTarget = (id, type) => {
        if (type === 'individual') {
            setTargets(prev => ({
                ...prev,
                individual_ids: prev.individual_ids.includes(id)
                    ? prev.individual_ids.filter(x => x !== id)
                    : [...prev.individual_ids, id]
            }));
        } else {
            setTargets(prev => ({
                ...prev,
                group_ids: prev.group_ids.includes(id)
                    ? prev.group_ids.filter(x => x !== id)
                    : [...prev.group_ids, id]
            }));
        }
    };

    const getZoneName = (zoneId) => zones.find(z => z.id === zoneId)?.name || 'Unknown';
    const getTriggerLabel = (trigger) => { const opt = TRIGGER_OPTIONS.find(o => o.id === trigger); return opt ? t(opt.labelKey) : trigger; };
    const getActionLabel = (actionType) => { const opt = ACTION_TYPE_OPTIONS.find(a => a.id === actionType); return opt ? t(opt.labelKey) : actionType; };

    const getTargetSummary = (rule) => {
        const targets = rule.targets || [];
        const individuals = targets.filter(x => x.device_user);
        const groupTargets = targets.filter(x => x.asset_group);
        if (individuals.length > 0) return t('gps.rules.targetSummaryIndividuals', {count: individuals.length});
        if (groupTargets.length > 0) return t('gps.rules.targetSummaryGroups', {count: groupTargets.length});
        return t('gps.rules.noTargets');
    };

    const getRuleActionSummary = (rule) => {
        const parts = [];
        if (rule.show_warning) parts.push(t('gps.rules.warning'));
        if (rule.action_type) parts.push(getActionLabel(rule.action_type));
        if (rule.send_sos) parts.push('SOS');
        return parts.length > 0 ? parts.join(' + ') : t('gps.rules.actionNone');
    };

    // Device types with GPS assets that are missing a SOS action
    // Helper: get unique device types from an asset list using gps_devices (not active_gps_device)
    const getDeviceTypesFromAssets = (assetList) => [...new Map(
        assetList.flatMap(a => (a.gps_devices || []).filter(d => d.device_type_id))
            .map(d => [d.device_type_id, {id: d.device_type_id, name: d.device_type_name}])
    ).values()];

    const registeredDeviceTypes = getDeviceTypesFromAssets(assets);
    const sosDeviceTypeIds = new Set(gpsActions.filter(a => a.is_sos).map(a => a.device_type));
    const deviceTypesMissingSos = registeredDeviceTypes.filter(dt => !sosDeviceTypeIds.has(dt.id));

    // Device types only from selected targets
    const selectedDeviceTypes = (() => {
        if (targets.type === 'individuals' && targets.individual_ids.length > 0) {
            return getDeviceTypesFromAssets(assets.filter(a => targets.individual_ids.includes(a.id)));
        }
        if (targets.type === 'groups' && targets.group_ids.length > 0) {
            return registeredDeviceTypes; // grupos: usar todos como fallback
        }
        return []; // nada seleccionado → sin advertencia
    })();
    const selectedMissingSos = selectedDeviceTypes.filter(dt => !sosDeviceTypeIds.has(dt.id));
    const selectedCoveredSos = selectedDeviceTypes.filter(dt => sosDeviceTypeIds.has(dt.id));

    const inputStyle = {width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px'};
    const labelStyle = {fontSize: '13px', color: '#64748B', display: 'block', marginBottom: '4px', marginTop: '12px'};

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={1}/>

            {ruleError && (
                <Modal show={true} setter={() => setRuleError(null)} name={t('common.error')} buttonName={t('common.confirm')} onClick={() => setRuleError(null)} hideCancel>
                    <p style={{margin: 0, fontSize: '14px'}}>{ruleError}</p>
                </Modal>
            )}

            <Modal show={!!deletingRuleId} setter={() => setDeletingRuleId(null)} name={t('gps.rules.deleteConfirm')} buttonName={t('common.confirm')} onClick={confirmDeleteRule} danger>
                <p style={{margin: 0, fontSize: '14px'}}>{t('gps.rules.deleteConfirm')}</p>
            </Modal>

            <Modal show={!!testResultMsg} setter={() => setTestResultMsg(null)} name={t('gps.rules.testSent')} buttonName={t('common.confirm')} onClick={() => setTestResultMsg(null)} hideCancel>
                <p style={{margin: 0, fontSize: '14px'}}>{testResultMsg}</p>
            </Modal>

            <Modal show={sosConfirmPending} setter={setSosConfirmPending} name={t('gps.sos.confirmTitle')} buttonName={t('common.confirm')} onClick={() => saveRule(true)}>
                <div style={{padding: '4px 0'}}>
                    <p style={{fontSize: '13px', color: '#334155', marginBottom: '10px'}}>
                        {t('gps.sos.confirmMessage')}
                    </p>
                    <ul style={{margin: '0 0 10px 0', paddingLeft: '18px'}}>
                        {deviceTypesMissingSos.map(dt => (
                            <li key={dt.id} style={{fontSize: '13px', color: '#DC2626'}}>{dt.name}</li>
                        ))}
                    </ul>
                    <p style={{fontSize: '12px', color: '#64748B', margin: 0}}>
                        {t('gps.sos.confirmContinue')}
                    </p>
                </div>
            </Modal>

            <Modal show={showModal} setter={setShowModal} name={editingRule ? t('gps.rules.editRule') : t('gps.rules.createRule')} buttonName={t('common.save')} onClick={() => saveRule()}>
                <TextInput form={form} setForm={setForm} name={'name'} placeholder={t('gps.rules.ruleName')}/>
                {formErrors.name && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '-6px', marginBottom: '4px'}}>{formErrors.name}</div>}

                <label style={labelStyle}>{t('gps.rules.zone')}</label>
                <select value={form.zone} onChange={e => setForm({...form, zone: e.target.value})} style={{...inputStyle, border: `1px solid ${formErrors.zone ? '#EF4444' : '#CBD5E1'}`}}>
                    <option value="">{t('gps.rules.selectZone')}</option>
                    {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
                {formErrors.zone && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.zone}</div>}

                <label style={labelStyle}>{t('common.trigger')}</label>
                <select value={form.trigger} onChange={e => setForm({...form, trigger: e.target.value})} style={inputStyle}>
                    {TRIGGER_OPTIONS.map(opt => <option key={opt.id} value={opt.id}>{t(opt.labelKey)}</option>)}
                </select>

                {form.trigger === 'overtime' && (
                    <>
                        <label style={labelStyle}>{t('gps.rules.overtimeMinutes')}</label>
                        <input
                            type="number" min="1" value={form.overtime_minutes}
                            onChange={e => setForm({...form, overtime_minutes: e.target.value})}
                            style={{...inputStyle, border: `1px solid ${formErrors.overtime_minutes ? '#EF4444' : '#CBD5E1'}`}}
                        />
                        {formErrors.overtime_minutes && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.overtime_minutes}</div>}
                        {overtimeWarning && (
                            <div style={{fontSize: '12px', color: '#F59E0B', marginTop: '4px'}}>{overtimeWarning}</div>
                        )}
                    </>
                )}

                {/* Targets */}
                <label style={labelStyle}>{t('gps.rules.applyTo')}</label>
                <div className="d-flex gap-2 mb-2">
                    <button
                        type="button"
                        onClick={() => setTargets({...targets, type: 'individuals'})}
                        style={{
                            ...inputStyle, width: 'auto', cursor: 'pointer',
                            backgroundColor: targets.type === 'individuals' ? '#1E293B' : '#F8FAFC',
                            color: targets.type === 'individuals' ? 'white' : '#334155',
                            border: '1px solid #CBD5E1',
                        }}
                    >
                        {t('gps.rules.individuals')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setTargets({...targets, type: 'groups'})}
                        style={{
                            ...inputStyle, width: 'auto', cursor: 'pointer',
                            backgroundColor: targets.type === 'groups' ? '#1E293B' : '#F8FAFC',
                            color: targets.type === 'groups' ? 'white' : '#334155',
                            border: '1px solid #CBD5E1',
                        }}
                    >
                        {t('gps.rules.groups')}
                    </button>
                </div>

                {targets.type === 'individuals' && (() => {
                    const q = assetSearch.toLowerCase();
                    const filtered = assets.filter(a =>
                        a.name.toLowerCase().includes(q) ||
                        (a.identification_number || '').toLowerCase().includes(q)
                    );
                    const filteredIds = filtered.map(a => a.id);
                    const allSelected = filteredIds.length > 0 && filteredIds.every(id => targets.individual_ids.includes(id));
                    return (
                        <div style={{marginBottom: '4px'}}>
                            <div className="d-flex gap-2 mb-1">
                                <input
                                    type="text"
                                    value={assetSearch}
                                    onChange={e => setAssetSearch(e.target.value)}
                                    placeholder={t('gps.rules.searchAssets')}
                                    style={{...inputStyle, flex: 1, padding: '6px 8px'}}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (allSelected) {
                                            setTargets(prev => ({...prev, individual_ids: prev.individual_ids.filter(id => !filteredIds.includes(id))}));
                                        } else {
                                            setTargets(prev => ({...prev, individual_ids: [...new Set([...prev.individual_ids, ...filteredIds])]}));
                                        }
                                    }}
                                    style={{padding: '6px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', background: allSelected ? '#FEF2F2' : '#F8FAFC', color: allSelected ? '#EF4444' : '#334155', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap'}}
                                >
                                    {allSelected ? t('gps.rules.deselectAll') : t('gps.rules.selectAll')}
                                </button>
                            </div>
                        </div>
                    );
                })()}

                <div style={{maxHeight: '150px', overflowY: 'auto', border: `1px solid ${formErrors.targets ? '#EF4444' : '#E2E8F0'}`, borderRadius: '6px'}}>
                    {targets.type === 'individuals' ? (() => {
                        const q = assetSearch.toLowerCase();
                        const filtered = assets.filter(a =>
                            a.name.toLowerCase().includes(q) ||
                            (a.identification_number || '').toLowerCase().includes(q)
                        );
                        return filtered.map(asset => (
                            <div
                                key={asset.id}
                                className="d-flex align-items-center justify-content-between p-2"
                                style={{borderBottom: '1px solid #F1F5F9', cursor: 'pointer'}}
                                onClick={() => toggleTarget(asset.id, 'individual')}
                            >
                                <div>
                                    <div style={{fontSize: '13px'}}>{asset.name}</div>
                                    {asset.identification_number && <div style={{fontSize: '11px', color: '#94A3B8'}}>{asset.identification_number}</div>}
                                </div>
                                {targets.individual_ids.includes(asset.id) && (
                                    <div style={{width: '16px', height: '16px', borderRadius: '3px', backgroundColor: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                                        <span style={{color: 'white', fontSize: '11px', fontWeight: 'bold'}}>&#10003;</span>
                                    </div>
                                )}
                            </div>
                        ));
                    })() : (
                        groups.map(group => (
                            <div
                                key={group.id}
                                className="d-flex align-items-center justify-content-between p-2"
                                style={{borderBottom: '1px solid #F1F5F9', cursor: 'pointer'}}
                                onClick={() => toggleTarget(group.id, 'group')}
                            >
                                <span style={{fontSize: '13px'}}>{group.name} ({group.members?.length || 0} members)</span>
                                {targets.group_ids.includes(group.id) && (
                                    <div style={{width: '16px', height: '16px', borderRadius: '3px', backgroundColor: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                        <span style={{color: 'white', fontSize: '11px', fontWeight: 'bold'}}>&#10003;</span>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
                {formErrors.targets && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.targets}</div>}

                {/* Actions section */}
                <div style={{marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E2E8F0'}}>
                    <div style={{fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '4px'}}>{t('gps.rules.actionsSection')}</div>
                    {formErrors.actions && <div style={{fontSize: '11px', color: '#EF4444', marginBottom: '8px'}}>{formErrors.actions}</div>}

                    {/* Warning checkbox */}
                    <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer', marginBottom: '10px'}}>
                        <input
                            type="checkbox"
                            checked={form.show_warning}
                            onChange={e => setForm({...form, show_warning: e.target.checked})}
                            style={{width: '18px', height: '18px'}}
                        />
                        <FontAwesomeIcon icon={faTriangleExclamation} color="#EF4444" size="sm"/>
                        <span style={{fontSize: '13px', color: '#334155'}}>{t('gps.rules.showWarningOnMap')}</span>
                    </label>

                    {/* Send notification checkbox */}
                    <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer'}}>
                        <input
                            type="checkbox"
                            checked={form.send_notification}
                            onChange={e => setForm({...form, send_notification: e.target.checked})}
                            style={{width: '18px', height: '18px'}}
                        />
                        <span style={{fontSize: '13px', color: '#334155'}}>{t('gps.rules.sendNotification')}</span>
                    </label>

                    {/* Send SOS checkbox */}
                    <label className="d-flex align-items-center gap-2" style={{cursor: 'pointer', marginTop: '8px'}}>
                        <input
                            type="checkbox"
                            checked={form.send_sos}
                            onChange={e => setForm({...form, send_sos: e.target.checked})}
                            style={{width: '18px', height: '18px', accentColor: '#EF4444'}}
                        />
                        <span style={{fontSize: '13px', fontWeight: '600', color: '#EF4444'}}>Mandar SOS</span>
                    </label>
                    {form.send_sos && selectedMissingSos.length > 0 && (
                        <div style={{marginTop: '6px', padding: '10px 12px', borderRadius: '6px', background: '#FFF5F5', border: '1px solid #FECACA'}}>
                            <p style={{margin: '0 0 6px 0', fontSize: '12px', color: '#DC2626', fontWeight: '600'}}>
                                ⚠ {t('gps.sos.warningTitle')}
                            </p>
                            <ul style={{margin: '0 0 6px 0', paddingLeft: '16px'}}>
                                {selectedMissingSos.map(dt => (
                                    <li key={dt.id} style={{fontSize: '12px', color: '#DC2626'}}>{dt.name}</li>
                                ))}
                            </ul>
                            <p style={{margin: 0, fontSize: '12px', color: '#92400E'}}>
                                {t('gps.sos.warningHint')}
                            </p>
                        </div>
                    )}

                    {/* Notification config (only if send_notification is checked) */}
                    {form.send_notification && (
                        <>
                            <label style={labelStyle}>{t('gps.rules.notificationType')}</label>
                            <select value={form.action_type} onChange={e => setForm({...form, action_type: e.target.value})} style={inputStyle}>
                                {visibleActionTypes.map(a => <option key={a.id} value={a.id}>{t(a.labelKey)}</option>)}
                            </select>

                            {/* Email */}
                            {parseInt(form.action_type) === 1 && (
                                <>
                                    <label style={labelStyle}>{t('gps.assets.emailAddress')}</label>
                                    <input value={form.action_data_1} onChange={e => setForm({...form, action_data_1: e.target.value})} style={{...inputStyle, border: `1px solid ${formErrors.action_data_1 ? '#EF4444' : '#CBD5E1'}`}} placeholder="recipient@email.com"/>
                                    {formErrors.action_data_1 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_1}</div>}
                                    <label style={labelStyle}>{t('gps.assets.subject')}</label>
                                    <PlaceholderInput value={form.action_data_2} onChange={v => setForm({...form, action_data_2: v})} style={{...inputStyle, border: `1px solid ${formErrors.action_data_2 ? '#EF4444' : '#CBD5E1'}`}} placeholder="Alert: {asset_name} {trigger} {zone_name}" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_2 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_2}</div>}
                                    <label style={labelStyle}>{t('gps.assets.body')}</label>
                                    <PlaceholderTextarea value={form.action_data_3} onChange={v => setForm({...form, action_data_3: v})} style={{...inputStyle, minHeight: '60px', border: `1px solid ${formErrors.action_data_3 ? '#EF4444' : '#CBD5E1'}`}} placeholder="{asset_name} has {trigger} zone {zone_name}" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_3 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_3}</div>}
                                </>
                            )}

                            {/* SMS */}
                            {parseInt(form.action_type) === 3 && systemSettings?.sms_enabled && (
                                <>
                                    <label style={labelStyle}>{t('gps.assets.phoneNumber')}</label>
                                    <PhoneInput
                                        country={'sn'}
                                        value={(form.action_data_1 || '').replace(/^\+/, '')}
                                        onChange={phone => setForm({...form, action_data_1: '+' + phone})}
                                    />
                                    {formErrors.action_data_1 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_1}</div>}
                                    <label style={labelStyle}>{t('gps.assets.message')}</label>
                                    <PlaceholderTextarea value={form.action_data_2} onChange={v => setForm({...form, action_data_2: v})} style={{...inputStyle, minHeight: '60px', border: `1px solid ${formErrors.action_data_2 ? '#EF4444' : '#CBD5E1'}`}} placeholder="{asset_name} has {trigger} zone {zone_name}" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_2 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_2}</div>}
                                </>
                            )}

                            {/* WhatsApp */}
                            {parseInt(form.action_type) === 4 && systemSettings?.whatsapp_enabled && (
                                <>
                                    <label style={labelStyle}>{t('gps.assets.phoneNumber')}</label>
                                    <PhoneInput
                                        country={'sn'}
                                        value={(form.action_data_1 || '').replace(/^\+/, '')}
                                        onChange={phone => setForm({...form, action_data_1: '+' + phone})}
                                    />
                                    {formErrors.action_data_1 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_1}</div>}
                                    <label style={labelStyle}>{t('gps.assets.message')}</label>
                                    <PlaceholderTextarea value={form.action_data_2} onChange={v => setForm({...form, action_data_2: v})} style={{...inputStyle, minHeight: '60px', border: `1px solid ${formErrors.action_data_2 ? '#EF4444' : '#CBD5E1'}`}} placeholder="{asset_name} has {trigger} zone {zone_name}" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_2 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_2}</div>}
                                </>
                            )}

                            {/* MQTT */}
                            {parseInt(form.action_type) === 5 && (
                                <>
                                    <label style={labelStyle}>{t('gps.assets.mqttConnection')}</label>
                                    <select value={form.mqtt_connection} onChange={e => setForm({...form, mqtt_connection: e.target.value})} style={{...inputStyle, border: `1px solid ${formErrors.mqtt_connection ? '#EF4444' : '#CBD5E1'}`}}>
                                        <option value="">{t('gps.assets.selectConnection')}</option>
                                        {mqttConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.host}</option>)}
                                    </select>
                                    {formErrors.mqtt_connection && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.mqtt_connection}</div>}
                                    <label style={labelStyle}>{t('gps.assets.topic')}</label>
                                    <PlaceholderInput value={form.action_data_1} onChange={v => setForm({...form, action_data_1: v})} style={{...inputStyle, border: `1px solid ${formErrors.action_data_1 ? '#EF4444' : '#CBD5E1'}`}} placeholder="alerts/gps" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_1 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_1}</div>}
                                    <JsonTextarea
                                        label={t('gps.assets.payload')}
                                        value={form.action_data_2}
                                        onChange={v => setForm({...form, action_data_2: v})}
                                        placeholder='{"asset":"{asset_name}","event":"{trigger}","zone":"{zone_name}"}'
                                        placeholders={RULE_PLACEHOLDERS}
                                    />
                                    <label style={labelStyle}>{t('gps.assets.boolFormat')}</label>
                                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${form.boolean_format === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: form.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC', color: form.boolean_format === opt.v ? '#4F46E5' : '#64748B', fontWeight: form.boolean_format === opt.v ? 600 : 400}}>
                                                <input type="radio" name="boolean_format_rule" value={opt.v} checked={form.boolean_format === opt.v} onChange={() => setForm({...form, boolean_format: opt.v})} style={{accentColor: '#4F46E5'}}/>
                                                {opt.l}
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* API */}
                            {parseInt(form.action_type) === 6 && (
                                <>
                                    <label style={labelStyle}>{t('gps.assets.apiConnection')}</label>
                                    <select value={form.api_connection} onChange={e => setForm({...form, api_connection: e.target.value})} style={{...inputStyle, border: `1px solid ${formErrors.api_connection ? '#EF4444' : '#CBD5E1'}`}}>
                                        <option value="">{t('gps.assets.selectConnection')}</option>
                                        {apiConnections.map(c => <option key={c.id} value={c.id}>{c.name || c.url}</option>)}
                                    </select>
                                    {formErrors.api_connection && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.api_connection}</div>}
                                    <label style={labelStyle}>{t('gps.assets.endpointPath')}</label>
                                    <PlaceholderInput value={form.action_data_1} onChange={v => setForm({...form, action_data_1: v})} style={{...inputStyle, border: `1px solid ${formErrors.action_data_1 ? '#EF4444' : '#CBD5E1'}`}} placeholder="/webhook/gps-alert" placeholders={RULE_PLACEHOLDERS}/>
                                    {formErrors.action_data_1 && <div style={{fontSize: '11px', color: '#EF4444', marginTop: '2px'}}>{formErrors.action_data_1}</div>}
                                    <JsonTextarea
                                        label={t('gps.assets.bodyJson')}
                                        value={form.action_data_2}
                                        onChange={v => setForm({...form, action_data_2: v})}
                                        placeholder='{"asset":"{asset_name}","event":"{trigger}","zone":"{zone_name}"}'
                                        placeholders={RULE_PLACEHOLDERS}
                                    />
                                    <label style={labelStyle}>{t('gps.assets.boolFormat')}</label>
                                    <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                                        {[{v: 'raw', l: '0 / 1'}, {v: 'bool', l: 'True / False'}, {v: 'lower', l: 'true / false'}].map(opt => (
                                            <label key={opt.v} style={{display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', cursor: 'pointer', padding: '4px 10px', borderRadius: '6px', border: `1px solid ${form.boolean_format === opt.v ? '#4F46E5' : '#E2E8F0'}`, background: form.boolean_format === opt.v ? '#EEF2FF' : '#F8FAFC', color: form.boolean_format === opt.v ? '#4F46E5' : '#64748B', fontWeight: form.boolean_format === opt.v ? 600 : 400}}>
                                                <input type="radio" name="boolean_format_rule" value={opt.v} checked={form.boolean_format === opt.v} onChange={() => setForm({...form, boolean_format: opt.v})} style={{accentColor: '#4F46E5'}}/>
                                                {opt.l}
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Active toggle */}
                <div className="d-flex align-items-center gap-2 mt-3">
                    <FontAwesomeIcon
                        icon={form.is_active ? faToggleOn : faToggleOff}
                        size="lg"
                        color={form.is_active ? '#10B981' : '#CBD5E1'}
                        style={{cursor: 'pointer'}}
                        onClick={() => setForm({...form, is_active: !form.is_active})}
                    />
                    <span style={{fontSize: '13px', color: '#64748B'}}>{form.is_active ? t('common.active') : t('common.inactive')}</span>
                </div>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <PageTitle title={t('common.gpsTracking')}/>
                <GPSTabs active={3}/>

                <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 style={{fontWeight: '600', margin: 0}}>{t('gps.rules.zoneRulesTitle', {count: rules.length})}</h6>
                    <button
                        onClick={openCreateModal}
                        style={{
                            padding: '8px 16px', borderRadius: '6px', border: 'none',
                            backgroundColor: '#1E293B', color: 'white', fontSize: '13px',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} size="sm"/> {t('gps.rules.newRule')}
                    </button>
                </div>

                <div className="d-flex flex-column gap-2">
                    {rules.map(rule => (
                        <div key={rule.id} style={{
                            padding: '16px',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            opacity: rule.is_active ? 1 : 0.6,
                        }}>
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        <div style={{fontWeight: '600', fontSize: '14px'}}>{rule.name}</div>
                                        <span style={{
                                            fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                                            backgroundColor: rule.is_active ? '#D1FAE5' : '#F1F5F9',
                                            color: rule.is_active ? '#065F46' : '#64748B',
                                        }}>
                                            {rule.is_active ? t('common.active') : t('common.inactive')}
                                        </span>
                                        {rule.show_warning && (
                                            <FontAwesomeIcon icon={faTriangleExclamation} color="#EF4444" size="sm" title="Visual warning enabled"/>
                                        )}
                                    </div>
                                    <div style={{fontSize: '13px', color: '#334155'}}>
                                        When target <strong>{getTriggerLabel(rule.trigger).toLowerCase()}</strong> "{getZoneName(rule.zone)}"
                                        {rule.trigger === 'overtime' && ` (${rule.effective_overtime || rule.overtime_minutes} min)`}
                                        {' → '}<strong>{getRuleActionSummary(rule)}</strong>
                                    </div>
                                    <div style={{fontSize: '12px', color: '#94A3B8', marginTop: '4px'}}>
                                        {t('gps.rules.targetsLabel')} {getTargetSummary(rule)}
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3">
                                    {corporateGroup?.dev_mode && rule.send_notification && (
                                        <FontAwesomeIcon
                                            icon={testingRuleId === rule.id ? faSpinner : faPlay}
                                            spin={testingRuleId === rule.id}
                                            color="#F59E0B"
                                            style={{cursor: 'pointer'}}
                                            title="Test this rule"
                                            onClick={() => testRule(rule.id)}
                                        />
                                    )}
                                    <FontAwesomeIcon
                                        icon={rule.is_active ? faToggleOn : faToggleOff}
                                        size="lg"
                                        color={rule.is_active ? '#10B981' : '#CBD5E1'}
                                        style={{cursor: 'pointer'}}
                                        onClick={() => toggleActive(rule)}
                                    />
                                    <FontAwesomeIcon
                                        icon={faEdit}
                                        color="#64748B"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => openEditModal(rule)}
                                    />
                                    <FontAwesomeIcon
                                        icon={faTrash}
                                        color="#EF4444"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => deleteRule(rule.id)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}

                    {rules.length === 0 && (
                        <div style={{textAlign: 'center', padding: '40px', color: '#94A3B8', fontSize: '14px'}}>
                            {t('gps.rules.noRulesYet')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
